import { useEffect, useRef, useState } from "react";
import { documentsApi, businessesApi } from "@/lib/api";
import { useAuth, isMaster, isBranchUser, userBusinessIds } from "@/lib/auth";
import StatusBadge from "@/components/dochub/StatusBadge";
import { Link } from "react-router-dom";
import { Search, Trash2, RefreshCcw, X } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "DRAFT_IN_DOCHUB", label: "Draft" },
  { key: "PUBLISHED", label: "Published" },
  { key: "DUPLICATE_SUSPECT", label: "Duplicates" },
  { key: "PROCESSING", label: "Processing" },
  { key: "ERROR", label: "Errors" },
  { key: "UNASSIGNED", label: "Unassigned", triage: true },
  { key: "", label: "All" },
];

const BULK_STATUSES = [
  { key: "DRAFT_IN_DOCHUB", label: "Draft" },
  { key: "DUPLICATE_SUSPECT", label: "Duplicate" },
  { key: "ERROR", label: "Error" },
];

export default function DocumentsInbox() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("DRAFT_IN_DOCHUB");
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [biz, setBiz] = useState("");
  const [loading, setLoading] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Multi-business users (or master admins) can filter by business. Branch
  // users and single-business admins get the filter hidden because it would
  // be a no-op (they only ever see docs from one business anyway).
  const canFilterBiz = isMaster(user) || userBusinessIds(user).length > 1;

  const load = () => {
    setLoading(true);
    const params = {};
    if (tab) params.status = tab;
    if (search) params.search = search;
    if (canFilterBiz && biz && tab !== "UNASSIGNED") params.business_id = biz;
    documentsApi.list(params).then((d) => {
      setDocs(d);
      // Drop any selections that are no longer in the list
      setSelected((prev) => {
        const ids = new Set(d.map((x) => x.id));
        const next = new Set();
        prev.forEach((id) => { if (ids.has(id)) next.add(id); });
        return next;
      });
    }).finally(() => setLoading(false));
  };

  // Keep a ref to the LATEST `load` so the 8s background poll doesn't call a
  // stale closure. Before this fix, `search` was captured at effect-creation
  // time and the polling loop kept re-firing load() with an empty search —
  // wiping the user's filtered results ~8 seconds after they typed a supplier
  // name and pressed Enter (user-reported iter100). By reading loadRef.current
  // inside the interval callback we always call today's `load` with today's
  // state.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => { businessesApi.list().then(setBusinesses); }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => loadRef.current(), 8000);
    return () => clearInterval(t);
  }, [tab, biz]); // eslint-disable-line react-hooks/exhaustive-deps

  const allSelected = docs.length > 0 && selected.size === docs.length;
  const someSelected = selected.size > 0;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(docs.map((d) => d.id)));
  };

  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, status) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const label = action === "delete" ? `Delete ${ids.length} document(s)?` : action === "retry" ? `Retry AI processing for ${ids.length} document(s)?` : `Move ${ids.length} document(s) to ${status}?`;
    if (!window.confirm(label)) return;
    setBulkBusy(true);
    try {
      const res = await documentsApi.bulk(ids, action, status);
      toast.success(action === "retry" ? `${res.changed} document(s) re-queued for processing` : `${res.changed} document(s) updated`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-4xl font-bold text-espresso" data-testid="documents-title">Document inbox</h1>
        <p className="text-espresso-ink/60 mt-2">Every receipt and invoice ingested by DocHub.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-2">
          {TABS.filter((t) => !t.triage || !isBranchUser(user)).map((t) => (
            <button
              key={t.key || "all"}
              data-testid={`tab-${t.key || "all"}`}
              onClick={() => { setTab(t.key); clearSelection(); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                tab === t.key
                  ? "bg-espresso text-canvas"
                  : "bg-white border border-canvas-border text-espresso-ink hover:bg-canvas-border/50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-3">
          {canFilterBiz && (
            <select
              data-testid="documents-biz-filter"
              value={biz}
              onChange={(e) => setBiz(e.target.value)}
              className="px-3 py-2 bg-white border border-canvas-border rounded-lg text-sm brand-focus"
            >
              <option value="">All businesses</option>
              {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-espresso-ink/40" />
            <input
              data-testid="documents-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search vendor or invoice #"
              className="pl-9 pr-3 py-2 bg-white border border-canvas-border rounded-lg text-sm brand-focus w-72"
            />
          </div>
        </div>
      </div>

      {someSelected && (
        <div
          data-testid="bulk-toolbar"
          className="sticky top-2 z-10 flex flex-wrap items-center gap-3 bg-espresso text-canvas rounded-xl px-5 py-3 shadow-soft"
        >
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">
            {selected.size} selected
          </span>
          <div className="h-4 w-px bg-canvas/30" />
          <div className="flex items-center gap-2 text-xs">
            <span className="uppercase tracking-widest text-canvas/70">Set status</span>
            {BULK_STATUSES.map((s) => (
              <button
                key={s.key}
                disabled={bulkBusy}
                onClick={() => runBulk("set_status", s.key)}
                data-testid={`bulk-set-${s.key}`}
                className="px-3 py-1.5 rounded-full bg-canvas/10 hover:bg-canvas/20 border border-canvas/20 text-canvas font-medium disabled:opacity-50"
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className="h-4 w-px bg-canvas/30" />
          <button
            disabled={bulkBusy}
            onClick={() => runBulk("retry")}
            data-testid="bulk-retry"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold text-espresso hover:opacity-90 text-xs font-semibold disabled:opacity-50"
            title="Re-run AI extraction on stuck (Processing) or failed (Error) documents"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Retry processing
          </button>
          <button
            disabled={bulkBusy}
            onClick={() => runBulk("delete")}
            data-testid="bulk-delete"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-status-duplicate text-white hover:opacity-90 text-xs font-semibold disabled:opacity-50"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
          <button
            disabled={bulkBusy}
            onClick={load}
            data-testid="bulk-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-canvas/10 hover:bg-canvas/20 border border-canvas/20 text-canvas text-xs font-semibold"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            onClick={clearSelection}
            data-testid="bulk-clear"
            className="ml-auto inline-flex items-center gap-1 text-canvas/70 hover:text-canvas text-xs"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        </div>
      )}

      <div className="bg-white border border-canvas-border rounded-2xl overflow-hidden shadow-soft">
        {loading && docs.length === 0 ? (
          <div className="p-14 text-center text-espresso-ink/50">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="p-14 text-center text-espresso-ink/50" data-testid="documents-empty">No documents found.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-canvas">
              <tr className="text-xs uppercase tracking-wider text-espresso-ink/60">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    data-testid="select-all-checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="w-4 h-4 accent-espresso cursor-pointer"
                    aria-label="Select all documents"
                  />
                </th>
                <th className="text-left px-6 py-3 font-semibold">Vendor</th>
                <th className="text-left px-6 py-3 font-semibold">Invoice #</th>
                <th className="text-left px-6 py-3 font-semibold">Date</th>
                <th className="text-left px-6 py-3 font-semibold">Total</th>
                <th className="text-left px-6 py-3 font-semibold">Source</th>
                <th className="text-left px-6 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => {
                const isSel = selected.has(d.id);
                return (
                  <tr
                    key={d.id}
                    className={`border-t border-canvas-border transition-colors ${isSel ? "bg-gold-soft/40" : "hover:bg-canvas"}`}
                  >
                    <td className="w-10 px-4 py-4">
                      <input
                        type="checkbox"
                        data-testid={`select-doc-${d.id}`}
                        checked={isSel}
                        onChange={() => toggleOne(d.id)}
                        className="w-4 h-4 accent-espresso cursor-pointer"
                        aria-label={`Select document ${d.id}`}
                      />
                    </td>
                    <td className="px-6 py-4">
                      <Link to={`/documents/${d.id}`} className="font-medium text-espresso hover:text-gold" data-testid={`doc-row-${d.id}`}>
                        {d.vendor_name || d.original_filename || "Untitled"}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-espresso-ink text-sm">{d.extracted?.invoice_number || "—"}</td>
                    <td className="px-6 py-4 text-espresso-ink text-sm">{d.extracted?.invoice_date || "—"}</td>
                    <td className="px-6 py-4 text-espresso font-semibold text-sm">
                      {d.extracted?.total ? `R ${d.extracted.total.toFixed(2)}` : "—"}
                    </td>
                    <td className="px-6 py-4 text-xs uppercase tracking-widest text-espresso-ink/50">{d.source}</td>
                    <td className="px-6 py-4"><StatusBadge status={d.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
